import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { buildUrl, isDefined } from '@trimble-gcs/common';
import { catchError, combineLatest, forkJoin, map, Observable, of } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { AuthState } from '../../../auth/auth.state';
import { ConnectRegion } from '../../../connect/connect.models';
import { ConnectService } from '../../../connect/connect.service';
import { ExternalFileId } from '../../../connect/models/external-file-id';
import { injectLogger } from '../../../logging/logger-injection';
import { ScandataService } from '../../../scandata/scandata.service';
import { PickerType } from '../../file-pickers/file-picker.models';
import { ImportFile, ImportFileStatus } from '../../import.models';
import { allowImportForFileType } from '../../import.service';
import {
  ConnectFileDetail,
  ConnectTreeItem,
  ConnectTreeItemType,
} from './connect-file-picker.models';
import { connectFilePickerTranslations } from './connect-file-picker.translations';

@Injectable({
  providedIn: 'root',
})
export class ConnectFilePickerService {
  private readonly logger = injectLogger('ConnectFilePickerService');
  private readonly http = inject(HttpClient);
  private readonly connectService = inject(ConnectService);
  private readonly scandataService = inject(ScandataService);
  private readonly translations = connectFilePickerTranslations();
  private readonly store = inject(Store);

  getTreeItems(region: ConnectRegion, folderId: string): Observable<ConnectTreeItem[]> {
    const token = this.store.selectSnapshot(AuthState.accessToken);
    const url = buildUrl(
      region.endpoint.url,
      `folders/${folderId}/items?tokenThumburl=true&sort=+name`,
    );
    return this.http
      .get<ConnectFileDetail[]>(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .pipe(
        map((files) =>
          files
            .filter((file) => file.type === 'FOLDER' || allowImportForFileType(file.name))
            .map((file) => this.createTreeItem(region, file, folderId)),
        ),
      );
  }

  mapToImportFiles(treeItems: ConnectTreeItem[]): Observable<ImportFile[]> {
    const importFiles$ = treeItems.map((treeItem) => {
      return this.mapToImportFile(treeItem).pipe(
        catchError((err) => {
          this.logger.error(`Error getting file download information from Connect`, {}, err);

          return of({
            id: uuidv4(),
            name: treeItem.name,
            size: treeItem.size,
            pickerType: PickerType.Connect,
            status: ImportFileStatus.Error,
            statusMessage: this.translations.errorGettingFileInfo.text,
          } satisfies ImportFile);
        }),
      );
    });

    return forkJoin(importFiles$);
  }

  private mapToImportFile(treeItem: ConnectTreeItem): Observable<ImportFile> {
    const scan$ = isDefined(treeItem.externalFileId)
      ? this.scandataService.getScanByExternalFileId(treeItem.externalFileId)
      : of(undefined);

    const downloadUrl$ = this.connectService.getFileDownloadUrlForRegion(
      treeItem.regionUrl!,
      treeItem.id,
      treeItem.versionId,
    );

    return combineLatest([scan$, downloadUrl$]).pipe(
      map(([scan, downloadInfo]) => {
        return {
          id: treeItem.id,
          name: treeItem.name,
          size: treeItem.size,
          pickerType: PickerType.Connect,
          downloadUrl: downloadInfo.url,
          externalFileId: treeItem.externalFileId?.id ?? undefined,
          alreadyIngested: isDefined(scan),
        } satisfies ImportFile;
      }),
    );
  }

  private createTreeItem(
    connectRegion: ConnectRegion,
    connectFileDetail: ConnectFileDetail,
    folderId: string,
  ): ConnectTreeItem {
    return {
      regionUrl: connectRegion.endpoint.url,
      versionId: connectFileDetail.versionId,
      externalFileId: new ExternalFileId(connectFileDetail),
      folderId,
      type:
        connectFileDetail.type === 'FOLDER' ? ConnectTreeItemType.folder : ConnectTreeItemType.file,
      id: connectFileDetail.id,
      name: connectFileDetail.name,
      size: connectFileDetail.size,
      modifiedBy: `${connectFileDetail.modifiedBy.firstName} ${connectFileDetail.modifiedBy.lastName}`,
      modifiedOn: isDefined(connectFileDetail.modifiedOn)
        ? new Date(connectFileDetail.modifiedOn)
        : undefined,
      selected: false,
    };
  }
}
